import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getSiteSettingsAdmin, updateSiteSettings } from "@/lib/site-settings.functions";
import { DEFAULT_SITE_SETTINGS, mergeSiteSettings } from "@/lib/site-settings";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PlaceholderShell } from "@/components/admin/PlaceholderShell";

export const Route = createFileRoute("/admin/settings")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Settings · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminSettings,
});

function AdminSettings() {
  const get = useServerFn(getSiteSettingsAdmin);
  const save = useServerFn(updateSiteSettings);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["site-settings-admin"], queryFn: () => get() });

  const [form, setForm] = useState(DEFAULT_SITE_SETTINGS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const s = q.data?.settings;
    if (!s) return;
    setForm(mergeSiteSettings(s as unknown as Record<string, unknown>));
  }, [q.data]);

  const set =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const persist = async () => {
    setBusy(true);
    try {
      await save({
        data: {
          ...form,
          thanks_skip_bullets: form.thanks_skip_bullets.map((b) => b.trim()).filter(Boolean),
        },
      });
      toast.success("Settings saved — live across the site");
      qc.invalidateQueries({ queryKey: ["site-settings-admin"] });
      qc.invalidateQueries({ queryKey: ["site-settings"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="max-w-2xl space-y-5">
        <div>
          <h2 className="font-display text-xl font-bold">Company details &amp; links</h2>
          <p className="text-sm text-muted-foreground">
            These values are used across the website, lead form results, and thank-you pages.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Company phone</Label>
          <Input id="phone" value={form.company_phone} onChange={set("company_phone")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Company email</Label>
          <Input id="email" type="email" value={form.company_email} onChange={set("company_email")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="app">Full application link</Label>
          <Input id="app" value={form.application_url} onChange={set("application_url")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cal">Calendar booking link</Label>
          <Input id="cal" value={form.calendar_url} onChange={set("calendar_url")} />
        </div>

        <div className="space-y-5 border-t border-border pt-6">
          <div>
            <h2 className="font-display text-xl font-bold">Thank-you page — "What happens next"</h2>
            <p className="text-sm text-muted-foreground">
              Shown on every product thank-you page (DSCR and all other loan types).
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tnh">Section heading</Label>
            <Input id="tnh" value={form.thanks_next_heading} onChange={set("thanks_next_heading")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tne">Bullet 1 — check your email</Label>
            <Textarea id="tne" rows={3} value={form.thanks_next_email_text} onChange={set("thanks_next_email_text")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tnp">Bullet 2 — loan officer contact</Label>
            <Textarea id="tnp" rows={2} value={form.thanks_next_phone_text} onChange={set("thanks_next_phone_text")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tnc">Bullet 3 — confirmation email</Label>
            <Textarea id="tnc" rows={2} value={form.thanks_next_confirmation_text} onChange={set("thanks_next_confirmation_text")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tnf">"Didn't get the email?" note</Label>
            <Textarea id="tnf" rows={2} value={form.thanks_next_email_fallback_text} onChange={set("thanks_next_email_fallback_text")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tcp">Callback prompt</Label>
            <Input id="tcp" value={form.thanks_callback_prompt} onChange={set("thanks_callback_prompt")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tcb">Callback button label</Label>
            <Input id="tcb" value={form.thanks_callback_button} onChange={set("thanks_callback_button")} />
          </div>
        </div>

        <div className="space-y-5 border-t border-border pt-6">
          <div>
            <h2 className="font-display text-xl font-bold">Thank-you page — "Skip the wait" card</h2>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tsh">Card heading</Label>
            <Input id="tsh" value={form.thanks_skip_heading} onChange={set("thanks_skip_heading")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tsb">Card copy</Label>
            <Textarea id="tsb" rows={3} value={form.thanks_skip_body} onChange={set("thanks_skip_body")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tsl">Benefit bullets (one per line, up to 8)</Label>
            <Textarea
              id="tsl"
              rows={4}
              value={form.thanks_skip_bullets.join("\n")}
              onChange={(e) =>
                setForm((f) => ({ ...f, thanks_skip_bullets: e.target.value.split("\n") }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tab">Application button label</Label>
            <Input id="tab" value={form.thanks_apply_button} onChange={set("thanks_apply_button")} />
          </div>
        </div>

        <div className="space-y-3 border-t border-border pt-6">
          <h2 className="font-display text-xl font-bold">DSCR pricing source</h2>
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <Label htmlFor="legacy-pricing" className="text-base">Use old pricing (direct Optimal Blue)</Label>
              <p className="mt-1 text-sm text-muted-foreground">
                OFF (default): rates come from the central pricing engine, with automatic backup to the old
                pricing if it ever can't answer. ON: rates come only from the old direct Optimal Blue pricing.
              </p>
            </div>
            <Switch
              id="legacy-pricing"
              checked={form.dscr_use_legacy_pricing}
              onCheckedChange={(v) => setForm((f) => ({ ...f, dscr_use_legacy_pricing: v }))}
            />
          </div>
        </div>

        <Button onClick={persist} disabled={busy || q.isLoading}>
          {busy ? "Saving…" : "Save settings"}
        </Button>
      </div>

      <PlaceholderShell
        title="Staff &amp; roles"
        description="Staff accounts, roles, and portal-wide preferences."
        checklist={[
          "Staff account list",
          "Grant or revoke admin and loan officer roles",
          "Notification recipients",
          "Brand and disclosure defaults",
        ]}
      />
    </div>
  );
}
